export const SET_CONVERSAS = (state, payload) => {
  state.conversas = payload
}

export const APPEND_CONVERSAS = (state, payload) => {
  if (!Array.isArray(payload) || payload.length === 0) return
  // Dedup por id: evita duplicatas quando o backend retorna itens que
  // já estão na primeira página (raia de sincronismo de sync/refresh).
  const existingIds = {}
  for (const c of state.conversas) {
    if (c.id != null) existingIds[c.id] = true
  }
  const newItems = []
  for (const c of payload) {
    if (c.id != null && existingIds[c.id]) continue
    newItems.push(c)
  }
  if (newItems.length > 0) {
    state.conversas = state.conversas.concat(newItems)
  }
}

// Identidade canônica de uma mensagem (ADR-3): o `whatsapp_message_id`
// é estável entre o eco do webhook/poll e a resposta do POST de envio;
// o `id` (PK do banco) é apenas o fallback (optimisticas e mensagens
// muito antigas sem wa_id). Deduplicar/reconciliar por essa chave evita
// que a mesma mensagem entre duas vezes (uma via poll, outra via REPLACE).
export function msgKey(m) {
  if (!m) return null
  return m.whatsapp_message_id || m.id || null
}

// Desempate determinístico para o sort quando `data_mensagem` empata
// (mesmo minuto/segundo). Sem isso a ordem "embaralha" entre cargas
// porque Array.sort não é estável para elementos iguais em todos os
// engines. Espelha a ordenação do backend (`data_mensagem, data_registro,
// id`) usando a chave canônica como proxy estável. Comparação por string
// porque ids podem ser numéricos (PK) ou string (wa_id/optimistic).
function compareTiebreaker(a, b) {
  const ka = String(msgKey(a) == null ? '' : msgKey(a))
  const kb = String(msgKey(b) == null ? '' : msgKey(b))
  if (ka < kb) return -1
  if (ka > kb) return 1
  return 0
}

// Sort cronológico por data_mensagem. Optimisticas ficam SEMPRE no fim
// até o REPLACE — clock skew client/server pode bagunçar a ordem com
// mensagens entrantes via SSE/polling.
function sortByDataMensagem(list) {
  return list.sort(function (a, b) {
    if (a._optimistic && !b._optimistic) return 1
    if (!a._optimistic && b._optimistic) return -1
    const da = parseDate(a.data_mensagem)
    const db = parseDate(b.data_mensagem)
    if (da !== db) return da - db
    return compareTiebreaker(a, b)
  })
}

function parseDate(str) {
  if (!str) return 0
  // Backend format: "DD/MM/YYYY HH:mm"
  const m = /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})/.exec(String(str))
  if (m) {
    return new Date(+m[3], +m[2] - 1, +m[1], +m[4], +m[5]).getTime()
  }
  const d = new Date(str)
  return Number.isNaN(d.getTime()) ? 0 : d.getTime()
}

export const SET_MENSAGENS = (state, payload) => {
  if (!Array.isArray(payload)) {
    state.mensagens = payload
    return
  }
  const seen = {}
  const deduped = []
  for (const item of payload) {
    // Dedup por `whatsapp_message_id` (fallback `id`): a mesma mensagem
    // pode vir com `id` diferente (eco do webhook vs. linha já gravada)
    // mas o mesmo wa_id — antes passava como duplicata.
    const key = msgKey(item)
    if (key != null && seen[key]) continue
    if (key != null) seen[key] = true
    deduped.push(item)
  }
  // Histórico importado tem IDs novos com datas antigas — ordena por data_mensagem.
  sortByDataMensagem(deduped)
  state.mensagens = deduped
}

// has_more da carga inicial (listarMensagens SEM before_id). Coerção a boolean
// para não guardar undefined quando o backend omite o campo (default true).
export const SET_MENSAGENS_HAS_MORE = (state, payload) => {
  state.mensagensHasMore = payload !== false
}

export const SET_SELECTED_CONVERSA = (state, payload) => {
  state.selectedConversa = payload
}

// Atualiza apenas `state.selectedConversa.contato` preservando a identidade
// de `selectedConversa`. Usar quando refresh de dados do contato (ex:
// participantes de grupo) NÃO deve ser interpretado como troca de conversa
// — watchers de `selectedConversa` (ex.: chat-window: reset de drafts,
// foco do input) não devem disparar nesses casos.
export const SET_SELECTED_CONVERSA_CONTATO = (state, contato) => {
  if (!state.selectedConversa || !contato) return
  state.selectedConversa.contato = contato
}

export const SET_LOADING_CONVERSAS = (state, payload) => {
  state.loadingConversas = payload
}

export const SET_LOADING_MORE_CONVERSAS = (state, payload) => {
  state.loadingMoreConversas = payload
}

export const SET_CONVERSAS_PAGE = (state, payload) => {
  state.conversasPage = payload
}

export const SET_CONVERSAS_HAS_MORE = (state, payload) => {
  state.conversasHasMore = payload
}

export const SET_LOADING_MENSAGENS = (state, payload) => {
  state.loadingMensagens = payload
}

export const ADD_MENSAGEM = (state, payload) => {
  if (!payload) return
  // Não inserir se já existe uma mensagem com a mesma chave canônica
  // (whatsapp_message_id || id). Cobre o caso do eco do poll chegar com
  // `id` diferente mas o mesmo `whatsapp_message_id` da que já está no store.
  const key = msgKey(payload)
  if (key != null) {
    for (const msg of state.mensagens) {
      if (msgKey(msg) === key) return
    }
  }
  const list = [].concat(state.mensagens, [payload])
  sortByDataMensagem(list)
  state.mensagens = list
}

export const UPDATE_CONVERSA = (state, payload) => {
  const index = state.conversas.findIndex((c) => c.id === payload.id)
  if (index !== -1) {
    state.conversas.splice(index, 1, { ...state.conversas[index], ...payload })
  }
}

export const SET_UPLOAD_PROGRESS = (state, payload) => {
  state.uploadProgress = payload
}

export const SET_REPLY_TO = (state, payload) => {
  state.replyTo = payload
}

export const SET_NOTE_MODE = (state, payload) => {
  state.noteMode = payload
}

export const SET_EDITING_MESSAGE = (state, payload) => {
  state.editingMessage = payload
}

export const UPDATE_MENSAGEM = (state, payload) => {
  const index = state.mensagens.findIndex(function (m) {
    return m.id === payload.id
  })
  if (index === -1) return
  const previous = state.mensagens[index]
  state.mensagens.splice(index, 1, { ...previous, ...payload })
  // Se ``data_mensagem`` mudou (ex.: poll trazendo ISO completo pra
  // substituir um timestamp truncado de syncs antigos), reordena a
  // lista pra a posição não ficar inconsistente com o novo valor —
  // sem isso a próxima inserção via ADD_MENSAGEM chama o sort e a
  // mensagem "pula" surpreendendo o usuário.
  if (
    payload &&
    'data_mensagem' in payload &&
    payload.data_mensagem !== previous.data_mensagem
  ) {
    sortByDataMensagem(state.mensagens)
  }
}

// Replace an optimistic message (identified by temp_id) with the real server
// response. A optimistic já está no fim da lista (porque _optimistic empurra
// pro fim no sortByDataMensagem), então o splice in-place mantém a posição
// cronológica correta da chegada local. NÃO re-ordenar aqui — o backend grava
// `data_mensagem` de entrada com timestamp do WhatsApp e de saída com
// timestamp do banco, e re-sortar agora poderia mover a saída pra antes de
// uma entrada que chegou cronologicamente antes (clock skew servidor).
export const REPLACE_OPTIMISTIC_MENSAGEM = (state, { temp_id, real }) => {
  if (!temp_id || !real) return
  const index = state.mensagens.findIndex(function (m) {
    return m.id === temp_id
  })
  if (index === -1) return
  const list = state.mensagens.slice()
  list.splice(index, 1, { ...real })
  state.mensagens = list
}

export const REMOVE_MENSAGEM = (state, id) => {
  if (!id) return
  const index = state.mensagens.findIndex(function (m) {
    return m.id === id
  })
  if (index !== -1) {
    state.mensagens.splice(index, 1)
  }
}

// Lookup maps {id→status} e {whatsapp_message_id→status} pra bulk update.
function buildStatusMaps(payload) {
  const byId = {}
  const byWaId = {}
  for (const item of payload) {
    if (!item) continue
    if (item.id != null) byId[item.id] = item.status
    if (item.whatsapp_message_id) byWaId[item.whatsapp_message_id] = item.status
  }
  return { byId, byWaId }
}

// Apply a bulk status update for outgoing messages.
// payload: [{id, whatsapp_message_id, status}]
export const APPLY_DELIVERY_STATUSES = (state, payload) => {
  if (!Array.isArray(payload) || payload.length === 0) return
  const { byId, byWaId } = buildStatusMaps(payload)
  let changed = false
  const list = state.mensagens
  for (let j = 0; j < list.length; j++) {
    const msg = list[j]
    if (msg.direcao !== 'saida') continue
    const newStatus = byId[msg.id] || byWaId[msg.whatsapp_message_id]
    if (newStatus && msg.status !== newStatus) {
      list.splice(j, 1, { ...msg, status: newStatus })
      changed = true
    }
  }
  if (changed) state.mensagens = list.flat()
}

export const PREPEND_MENSAGENS = (state, payload) => {
  if (!Array.isArray(payload) || payload.length === 0) return
  const existingIds = {}
  for (const msg of state.mensagens) {
    existingIds[msg.id] = true
  }
  const newMsgs = []
  for (const msg of payload) {
    if (!existingIds[msg.id]) {
      newMsgs.push(msg)
    }
  }
  const merged = newMsgs.concat(state.mensagens)
  sortByDataMensagem(merged)
  state.mensagens = merged
}

export const SET_RESPOSTAS_RAPIDAS = (state, payload) => {
  state.respostasRapidas = payload
}

export const SET_FILTRO_STATUS = (state, payload) => {
  state.filtroStatus = payload
}

export const SET_FILTRO_PROFISSIONAL = (state, payload) => {
  state.filtroProfissional = payload
}

export const SET_LINKED_TASKS = (state, payload) => {
  state.linkedTaskByMessageId = payload || {}
}

export const SET_LINKED_TASK_LIST = (state, payload) => {
  state.linkedTaskList = Array.isArray(payload) ? payload : []
}

export const SET_NOTAS_CONVERSA = (state, payload) => {
  state.notasConversa = Array.isArray(payload) ? payload : []
}

export const SET_LOADING_NOTAS = (state, payload) => {
  state.loadingNotas = !!payload
}
